import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from 'react'
import { Modal } from './ui'

type Ask = { title: string; body?: ReactNode; confirm?: string; cancel?: string; danger?: boolean }
const Ctx = createContext<(a: Ask) => Promise<boolean>>(async () => false)

/**
 * An in-app confirmation for destructive or consequential actions. The native confirm() box is
 * drawn by the browser (and styled by the OS); this one matches the app and says exactly what
 * will happen. `await confirm({...})` resolves true only when the person confirms.
 */
export const useConfirm = () => useContext(Ctx)

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [ask, setAsk] = useState<Ask | null>(null)
  const resolve = useRef<((ok: boolean) => void) | null>(null)
  const confirm = useCallback(
    (a: Ask) =>
      new Promise<boolean>(r => {
        resolve.current?.(false)
        resolve.current = r
        setAsk(a)
      }),
    [],
  )
  const done = (ok: boolean) => {
    resolve.current?.(ok)
    resolve.current = null
    setAsk(null)
  }
  return (
    <Ctx.Provider value={confirm}>
      {children}
      {ask && (
        <Modal
          title={ask.title}
          onClose={() => done(false)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => done(false)} autoFocus>
                {ask.cancel ?? 'Cancel'}
              </button>
              <button type="button" className={`btn ${ask.danger ? 'danger-solid' : 'primary'}`} onClick={() => done(true)}>
                {ask.confirm ?? 'Confirm'}
              </button>
            </>
          }
        >
          {ask.body ? <div className="small muted">{ask.body}</div> : null}
        </Modal>
      )}
    </Ctx.Provider>
  )
}
